import { format, parseISO } from 'date-fns'
import { Decimal } from 'decimal.js'

export function useFormatTools() {
  const formatMoney = (value, decimal_places) => {
    const decimalPlaces = decimal_places == undefined ? 2 : decimal_places
    const division_value = 10 ** decimalPlaces
    return (value / division_value)
      .toFixed(decimalPlaces)
      .replace('.', ',')
      .replace(/(\d)(?=(\d{3})+,)/g, '$1.')
  }

  const truncDecimals = (value, decimal_places) => {
    let decimalPlaces = 2
    if (decimal_places != undefined) {
      decimalPlaces = decimal_places
    }
    const decimalValue = new Decimal(`${value}`)

    // Trunca para o número de casas decimais desejado
    const divisor = new Decimal(10).pow(decimalPlaces)
    const truncatedValue = decimalValue.mul(divisor).floor().div(divisor)

    return truncatedValue
  }

  const formatMoneyDecimals = (value, decimal_places) => {
    let decimalPlaces = 2
    if (decimal_places != undefined) {
      decimalPlaces = decimal_places
    }

    if (value == undefined) {
      return '0,00'
    }

    const truncatedValue = truncDecimals(value, decimalPlaces)

    const stringValue = truncatedValue.toFixed(decimalPlaces).toString()

    const maskedValue = stringValue.replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.')

    return maskedValue
  }

  const formatMoneyDecimalsWithSymbol = (value, decimal_places) => {
    const maskedValue = formatMoneyDecimals(value, decimal_places)

    return `R$ ${maskedValue}`
  }

  const formatMoneyDecimals2 = formatMoneyDecimals

  const formatClaimCode = claim_code => {
    return `${claim_code.substring(0, 3)}-${claim_code.substring(3, 7)}-${claim_code.substring(7, 10)}`
  }

  const formatInvestmentRisk = risk => {
    const riskParsedASCII = Math.floor(risk / 200) + 65
    return String.fromCharCode(riskParsedASCII)
  }

  const today = () => {
    return format(new Date(), 'yyyy-MM-dd').now().toISODate()
  }

  const isDocument = document => {
    const docRegex =
      /(^\d{3}\.\d{3}\.\d{3}-\d{2}$)|(^\d{11}$)|(^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$)|(^\d{14}$)/gm

    return docRegex.test(document.trim())
  }

  const formatDocument = value => {
    if (!value) return ''
    const formatedString = value.replace(/[^0-9]/g, '')
    if (value.length === 11)
      return formatedString.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    return formatedString.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }

  const formatPhone = value => {
    if (!value) return ''
    const formatedString = value.replace(/^(\+55)?(\d{2})(\d{5})(\d{4})$/, '+55 ($2) $3-$4')
    return formatedString
  }

  const formatMinimalID = value => value?.substring(0, 8) || ''

  const formatDateTime = (value, time = false) => {
    if (value == undefined) return ''
    const dataHora = value
    const data = dataHora.split('T')[0].split('-').reverse().join('/')
    const hora = dataHora.split('T')[1].split('.')[0]
    if (time) return `${data} - ${hora}`
    return data
  }

  const fromISOToDateString = value => {
    return format(parseISO(value), 'dd/MM/yyyy')
  }

  const formatDateTimeString = value => {
    return format(value, "dd/MM/yyyy 'às' HH:mm:ss")
  }

  const formatDate = value => {
    if (value == undefined) return ''
    return value.split('-').reverse().join('/')
  }

  const capitalize = word => {
    const lower = word.toLowerCase()
    return word.charAt(0).toUpperCase() + lower.slice(1)
  }

  const unMaskField = _ => {
    return _.replace(/[^\d]/g, '')
  }

  const normalizeText = _ => {
    return _.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  }

  const trunkText = (text, amount = 120) => {
    try {
      return text.substring(0, amount)
    } catch {
      return text
    }
  }

  const stringifyQuery = filters => {
    let query = '?'
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        switch (typeof value) {
          case 'object':
            if (Array.isArray(value) && value.toString().length) {
              query += `${key}=${value}&`
            }
            break
          default:
            query += `${key}=${value}&`
        }
      }
    })

    return query
  }

  const formatFineFromWallet = fine => {
    if (!fine) return 'R$ 0,00'
    const { value, charge_type } = fine

    let toReturn = 'R$ 0,00'

    if (charge_type && charge_type === 'f') {
      toReturn = `R$ ${formatMoney(value)}`
    }
    if (charge_type && charge_type === 'p') {
      toReturn = `${formatMoney(value)}%`
    }

    return toReturn
  }

  const formatInterestFromWallet = interest => {
    if (!interest) return 'R$ 0,00'
    const { value, charge_type } = interest

    let toReturn = 'R$ 0,00'

    const chargeTypeSuffixes = {
      df: 'a.d',
      mf: 'a.m',
      dp: 'a.d',
      mp: 'a.m',
    }

    if (charge_type && chargeTypeSuffixes[charge_type]) {
      if (charge_type.includes('f')) {
        toReturn = `R$ ${formatMoney(value)} ${chargeTypeSuffixes[charge_type]}`
      } else {
        toReturn = `${formatMoney(value)} % ${chargeTypeSuffixes[charge_type]}`
      }
    }
    return toReturn
  }

  const formatInvoiceType = invoiceType => {
    const invoiceTypes = {
      APS: 'APS - APÓLICE DE SEGURO',
      BA: 'BA - BOLETO APORTE',
      BP: 'BP - BOLETO PROPOSTA',
      CH: 'CH - CHEQUE',
      CC: 'CC - CARTÃO DE CRÉDITO',
      DAE: 'DAE - DIVIDA ATIVA DE ESTADO',
      DAM: 'DAM - DIVIDA ATIVA DE MUNICIPIO',
      DAU: 'DAU - DIVIDA ATIVA DA UNIÃO',
      DM: 'DM - DUPLICATA MERCANTIL',
      DMI: 'DMI - DUPLICATA MTIL POR INDICAÇÃO',
      DR: 'DR - DUPLICATA RURAL',
      DS: 'DS - DUPLICATA DE SERVIÇO',
      DSI: 'DSI - DUPLICATA DE SRVC P/INDICACAO',
      FAT: 'FAT - FATURA',
      LC: 'LC - LETRA DE CÂMBIO',
      ME: 'ME - MENSALIDADE ESCOLAR',
      NCC: 'NCC - NOTA DE CRÉDITO COMERCIAL',
      NCE: 'NCE - NOTA DE CRÉDITO À EXPORTAÇÃO',
      NCI: 'NCI - NOTA DE CRÉDITO INDUSTRIAL',
      NCR: 'NCR - NOTA DE CRÉDITO RURAL',
      ND: 'ND - NOTA DE DÉBITO',
      NP: 'NP - NOTA PROMISSÓRIA',
      NPR: 'NPR - NOTA PROMISSÓRIA RURAL',
      NS: 'NS - NOTA DE SEGURO',
      PCO: 'PCO - PARCELA DE CONSÓRCIO',
      RCO: 'RCO - RECIBO',
      TM: 'TM - TRIPLICATA MERCANTIL',
      TS: 'TS - TRIPLICATA DE SERVIÇO',
      OUT: 'OUT - OUTROS',
    }

    return invoiceTypes[invoiceType]
  }

  const formatDiscountsFromWallet = discounts => {
    if (discounts) {
      let results = ''
      let isPorcentagem = false
      for (let i = 0; i < discounts.length; i += 1) {
        results += formatMoney(discounts[i]?.value || '-')
        results += i < discounts.length - 1 ? ' / ' : ''
        isPorcentagem = discounts[i].charge_type === 'p'
      }
      return isPorcentagem ? `${results} %` : `R$ ${results}`
    }
    return 0
  }

  const ExtractMessagesFromErrorResponse = (err, parseToText = false) => {
    const data =
      err?.response?.data ||
      'Erro desconhecido ao realizar essa ação. Por favor entre em contato com o suporte'

    if (data?.status_code !== undefined) {
      delete data.status_code
    }

    const recursiveExtractMessages = item => {
      let messages = []
      if (Array.isArray(item)) {
        const flat_mapped = item.flatMap(x => recursiveExtractMessages(x))
        messages = messages.concat(flat_mapped)
      } else if (item === Object(item)) {
        messages = messages.concat(recursiveExtractMessages(Object.values(item)))
      } else {
        messages.push(item)
      }
      return messages
    }

    const messages = recursiveExtractMessages(data)

    if (!parseToText) {
      return messages
    }

    if (messages.length === 1) {
      return messages[0]
    }
    return `${messages.slice(0, -1).join(', ')} e ${messages.slice(-1)[0]}`
  }

  const formatAttributesToSelect = (params, key = 'name', label = 'label') => {
    if (!Array.isArray(params)) {
      throw new Error('The provided parameters must be an array')
    }

    return params.map(param => ({
      value: param[key],
      label: param[label],
    }))
  }

  const formatRawOptionsToLabels = (options, raw, key = 'name', label = 'label') => {
    return raw.map(item => {
      const found = options.find(opt => opt[key] === item)
      return found ? found[label] : item
    })
  }

  const toRaw = data => {
    return JSON.parse(JSON.stringify(data))
  }

  const formatContactPhone = value => {
    const regex = /^(\+\d{2})\((\d{2})\)(\d{5})(\d{4})$/
    // retira espaços e mantém os parênteses na formatação
    const cleaned = value.replace(/[^\d+()]/g, '')
    // formata o numero de acordo com o formato BR
    const formattedValue = cleaned.replace(regex, '$1 ($2) $3-$4')

    return formattedValue || value
  }

  const formatZipCode = value => {
    if (!value) return ''
    const cleaned = value.replace(/\D/g, '')
    return cleaned.replace(/^(\d{5})(\d{3})$/, '$1-$2')
  }

  return {
    formatMoney,
    formatMoneyDecimals,
    formatMoneyDecimals2,
    formatMoneyDecimalsWithSymbol,
    truncDecimals,
    formatClaimCode,
    formatInvestmentRisk,
    today,
    isDocument,
    formatDocument,
    formatPhone,
    formatMinimalID,
    formatDateTime,
    fromISOToDateString,
    formatDate,
    capitalize,
    unMaskField,
    normalizeText,
    trunkText,
    stringifyQuery,
    formatFineFromWallet,
    formatInterestFromWallet,
    formatInvoiceType,
    formatDiscountsFromWallet,
    ExtractMessagesFromErrorResponse,
    formatAttributesToSelect,
    formatRawOptionsToLabels,
    toRaw,
    formatContactPhone,
    formatZipCode,
    formatDateTimeString,
  }
}
